import React, { createContext, useState, useEffect, useContext } from "react";
import client from "../api/client";
import { useAuth } from "./AuthContext";

export interface InHouseRecord {
  userId: number;
  role: string;
  name: string;
  permissions: {
    canEdit: string[];
    canView: string[];
  };
  routes: string[];
  /** Персональные страницы поверх ролевых (см. utils/roleAccess.ts). */
  extraRoutes: string[];
}

interface InHouseContextData {
  inHouseRecords: Record<string, InHouseRecord>;
  currentUserRecord: InHouseRecord | null;
  loading: boolean;
}

const InHouseContext = createContext<InHouseContextData | undefined>(undefined);

export const InHouseProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [inHouseRecords, setInHouseRecords] = useState<
    Record<string, InHouseRecord>
  >({});
  const [currentUserRecord, setCurrentUserRecord] =
    useState<InHouseRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const { currentUser, loading: authLoading } = useAuth();

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;

    // No user = not logged in, stop loading
    if (!currentUser) {
      setCurrentUserRecord(null);
      setLoading(false);
      return;
    }

    // User exists, fetch staff data
    setLoading(true);
    const fetchStaff = async () => {
      try {
        const { data: me } = await client.get("/staff/me");
        const record: InHouseRecord = {
          userId: me.id,
          role: me.role,
          name: me.name,
          permissions: {
            canEdit: me.canEdit || [],
            canView: me.canView || [],
          },
          routes: me.routes || [],
          extraRoutes: me.extraRoutes || [],
        };
        setCurrentUserRecord(record);

        if (me.role === "admin") {
          const { data: all } = await client.get("/staff");
          const records: Record<string, InHouseRecord> = {};
          all.forEach((s: any) => {
            records[String(s.id)] = {
              userId: s.id,
              role: s.role,
              name: s.name,
              permissions: {
                canEdit: s.canEdit || [],
                canView: s.canView || [],
              },
              routes: s.routes || [],
              extraRoutes: s.extraRoutes || [],
            };
          });
          setInHouseRecords(records);
        } else {
          // Не-админам полный /staff недоступен, но имена/роли коллег нужны
          // для дропдаунов (менеджер/редактор/логист в карточке юзера).
          // /staff/directory отдаёт только id/name/role; права там пустые,
          // поэтому собственную запись перекрываем полной из /staff/me.
          const { data: directory } = await client.get("/staff/directory");
          const records: Record<string, InHouseRecord> = {};
          directory.forEach((s: any) => {
            records[String(s.id)] = {
              userId: s.id,
              role: s.role,
              name: s.name,
              permissions: { canEdit: [], canView: [] },
              routes: [],
              extraRoutes: [],
            };
          });
          records[String(record.userId)] = record;
          setInHouseRecords(records);
        }
      } catch (err) {
        console.error("Error fetching staff data:", err);
        setCurrentUserRecord(null);
      } finally {
        setLoading(false);
      }
    };

    fetchStaff();
  }, [currentUser, authLoading]);

  return (
    <InHouseContext.Provider
      value={{ inHouseRecords, currentUserRecord, loading }}
    >
      {children}
    </InHouseContext.Provider>
  );
};

export const useInHouse = (): InHouseContextData => {
  const context = useContext(InHouseContext);
  if (!context) {
    throw new Error("useInHouse must be used within an InHouseProvider");
  }
  return context;
};
